import { lazy } from 'react'
import { Navigate } from 'react-router-dom'

import {
  PoTemplateDetailDocument,
  ProcurementSettingsFeatureCheckDocument,
  RequestTypeDetailDocument,
  TaskFormDetailDocument,
} from 'generated/resources/graphql'

import i18n from 'modules/core/lib/i18n'
import { buildTaskFormRouteHeaderProps } from 'modules/procurement/pages/TaskFormDetailPage/taskFormHeader'

import { NavigationRoutesFn } from 'modules/core/lib/react-router'

const PurchaseRequestsSettingsPage = lazy(() => import('modules/procurement/pages/SettingsPage'))

const RequestTypeDetailPage = lazy(() => import('modules/procurement/pages/RequestTypeDetailPage'))

const PoTemplateDetailPage = lazy(() => import('modules/procurement/pages/PoTemplateDetailPage'))

const TaskFormDetailPage = lazy(() => import('modules/procurement/pages/TaskFormDetailPage'))

const settingsRoutes: NavigationRoutesFn = (navigation) => {
  return [
    {
      path: navigation.settings.procurement.path,
      handle: {
        meta: () => ({
          title: i18n.t('company_settings.finance.procurement.title'),
          illustration: 'receipt',
        }),
      },
      children: [
        {
          index: true,
          element: <Navigate to={navigation.settings.procurement.requests.path} replace />,
        },
        {
          path: navigation.settings.procurement.requests.path,
          element: <PurchaseRequestsSettingsPage />,
          handle: {
            meta: ({ watchQuery, session }) => {
              return watchQuery({
                query: ProcurementSettingsFeatureCheckDocument,
                variables: { companyId: session.company.id },
              }).map(({ data }) => {
                const isProcurementV2 = !!data?.features?.featuresConnection?.edges?.some(
                  (e) => e.node.name === 'procurement_v2' && e.node.enabled
                )

                return {
                  title: isProcurementV2
                    ? i18n.t('procurement.purchase_requests.settings.sidebar.request_types')
                    : i18n.t('procurement.purchase_requests.settings.sidebar.requests'),
                }
              })
            },
          },
        },
        {
          path: navigation.settings.procurement.taskForms.path,
          handle: {
            meta: () => ({
              title: i18n.t('procurement.purchase_requests.settings.sidebar.forms'),
            }),
          },
          children: [
            {
              index: true,
              element: <PurchaseRequestsSettingsPage />,
            },
            {
              path: navigation.settings.procurement.taskForms.id.path,
              children: [
                {
                  index: true,
                  element: <Navigate to="edit" replace />,
                },
                {
                  path: navigation.settings.procurement.taskForms.id.edit.path,
                  element: <TaskFormDetailPage />,
                  handle: {
                    resource: true,
                    meta: ({ watchQuery, params, session }) => {
                      return watchQuery({
                        query: TaskFormDetailDocument,
                        variables: {
                          id: String(params.id),
                          companyId: session.company.id,
                        },
                      }).map(({ data }) => {
                        const taskForm = data?.procurement?.taskForm
                        const form = taskForm?.formsConnection?.edges?.[0]?.node

                        return {
                          title:
                            taskForm?.name ||
                            i18n.t(
                              'procurement.purchase_requests.settings.task_forms.detail.breadcrumb'
                            ),
                          headerComponentProps: form
                            ? buildTaskFormRouteHeaderProps(form)
                            : undefined,
                        }
                      })
                    },
                    to: navigation.settings.procurement.taskForms.fullPath(),
                  },
                },
              ],
            },
          ],
        },

        {
          path: navigation.settings.procurement.poTemplates.path,
          element: <PurchaseRequestsSettingsPage />,
          handle: {
            meta: () => ({
              title: i18n.t('procurement.purchase_requests.settings.sidebar.po_templates'),
            }),
          },
        },
        {
          path: navigation.settings.procurement.poTemplate.path,
          handle: {
            meta: () => ({
              title: i18n.t('procurement.purchase_requests.settings.sidebar.po_templates'),
              backLink: { href: navigation.settings.procurement.poTemplates.fullPath() },
            }),
          },
          children: [
            {
              index: true,
              element: (
                <Navigate to={navigation.settings.procurement.poTemplates.fullPath()} replace />
              ),
            },
            {
              path: navigation.settings.procurement.poTemplate.id.path,
              element: <PoTemplateDetailPage />,
              handle: {
                resource: true,
                meta: ({ watchQuery, params }) => {
                  return watchQuery({
                    query: PoTemplateDetailDocument,
                    variables: {
                      poTemplateId: String(params.id),
                    },
                  }).map(({ data }) => {
                    const poTemplate = data?.procurement?.poTemplatesConnection?.edges?.[0]?.node

                    return {
                      title:
                        poTemplate?.name ||
                        i18n.t(
                          'procurement.purchase_requests.settings.po_templates.detail.breadcrumb'
                        ),
                      illustration: 'receipt',
                    }
                  })
                },
              },
            },
          ],
        },
        {
          path: navigation.settings.procurement.requestType.path,
          handle: {
            meta: ({ watchQuery, session }) => {
              return watchQuery({
                query: ProcurementSettingsFeatureCheckDocument,
                variables: { companyId: session.company.id },
              }).map(({ data }) => {
                const isProcurementV2 = !!data?.features?.featuresConnection?.edges?.some(
                  (e) => e.node.name === 'procurement_v2' && e.node.enabled
                )

                return {
                  title: isProcurementV2
                    ? i18n.t('procurement.purchase_requests.settings.sidebar.request_types')
                    : i18n.t('procurement.purchase_requests.settings.sidebar.requests'),
                  backLink: { href: navigation.settings.procurement.requests.fullPath() },
                }
              })
            },
          },
          children: [
            {
              index: true,
              element: (
                <Navigate to={navigation.settings.procurement.requests.fullPath()} replace />
              ),
            },
            {
              path: navigation.settings.procurement.requestType.id.path,
              element: <RequestTypeDetailPage />,
              handle: {
                resource: true,
                meta: ({ watchQuery, params, session }) => {
                  return watchQuery({
                    query: RequestTypeDetailDocument,
                    variables: {
                      requestTypeId: String(params.id),
                      companyId: session.company.id,
                    },
                  }).map(({ data }) => {
                    const requestType = data?.procurement?.typesConnection?.edges?.[0]?.node

                    return {
                      title:
                        requestType?.name ||
                        i18n.t('procurement.purchase_requests.settings.types.detail.breadcrumb'),
                      illustration: 'receipt',
                    }
                  })
                },
              },
            },
          ],
        },
      ],
    },
  ]
}

export default settingsRoutes
